import {
  ApiError,
  addCustomerPhoneSchema,
  CLOSED_ORDER_STATUSES,
  type Customer,
  type CustomerOrigin,
  type CustomerPhone,
  formatMoney,
  formatUsPhone,
  ORDER_TRANSITIONS,
  type OrderStatus,
  queryKeys,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  humanizeStatus,
  Input,
  ORDER_STATUS_TONE,
  RENTAL_STATUS_TONE,
  Skeleton,
  Switch,
  UNIT_STATUS_TONE,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import {
  ArrowLeft,
  CalendarClock,
  Pencil,
  Phone,
  Plus,
  ShoppingCart,
  Smartphone,
  Trash2,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { CopyButton } from '../components/copy-button.js'
import {
  addressDraftFrom,
  addressFromDraft,
  CustomerAddressFields,
} from '../components/customer-address-fields.js'
import { CustomerNotesCard, PinnedNotes } from '../components/customer-notes-card.js'
import { CustomerPlansCard } from '../components/customer-plans-card.js'
import { CustomerSupportTicketsCard } from '../components/customer-support-tickets-card.js'
import { LinkedCustomersCard } from '../components/linked-customers-card.js'
import { LinkedTasksCard } from '../components/linked-tasks-card.js'
import { MarkImportantButton } from '../components/mark-important.js'
import { MessageCustomerButton } from '../components/message-customer-dialog.js'
import { actionLabel, OrderTransitionDialog } from '../components/order-transition-dialog.js'
import { SavedCardsCard } from '../components/saved-cards-card.js'
import { CallButton } from '../components/softphone/call-button.js'
import { StoreCreditCard } from '../components/store-credit-card.js'
import { UnitAttributeBadges } from '../components/unit-attributes.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

interface OrderRow {
  id: string
  orderNumber: string
  status: OrderStatus
  channel: string
  totalCents: number
  balanceDueCents: number
  createdAt: string
}

/**
 * The two moves the counter actually reaches for on this screen — take
 * payment, mark it picked up. Derived from the shared state machine rather
 * than hardcoded per status, so a change to the machine doesn't leave a
 * dangling button here. Cancel and refund deliberately never appear: this is
 * "mark it paid and picked up", not order editing.
 *
 * A `paid` order has no direct move to `completed` (it must pass through
 * `fulfilling` first) — `via` carries that hop so the one button a counter
 * needs still takes one click.
 */
function counterAction(status: OrderStatus): { to: OrderStatus; via?: OrderStatus } | null {
  if (ORDER_TRANSITIONS[status]?.includes('paid')) return { to: 'paid' }
  if (status === 'paid') return { to: 'completed', via: 'fulfilling' }
  if (ORDER_TRANSITIONS[status]?.includes('completed')) return { to: 'completed' }
  return null
}

const ORIGIN_LABEL: Record<CustomerOrigin, string> = {
  admin: 'Added in admin',
  web: 'Web signup',
  kiosk: 'Kiosk',
  import: 'Imported',
}

interface DeviceRow {
  id: string
  imei: string
  productName: string
  sku: string
  status: string
  orderId: string | null
  orderNumber: string | null
  soldAt: string | null
  attributes: Record<string, string | number | boolean>
}

interface RentalRow {
  id: string
  rentalNumber: string
  status: string
  imei: string
  productName: string
  dueDate: string | null
}

/**
 * Everything the shop knows about one customer, on one page — because the
 * question at the counter is almost always "what did they buy and which phone
 * is theirs", and answering it should not need three screens.
 */
export function CustomerDetailPage() {
  const { customerId } = useParams({ from: '/authed/customers/$customerId' })
  const { can } = useAuth()
  const [editing, setEditing] = useState(false)
  const [transition, setTransition] = useState<{
    order: OrderRow
    to: OrderStatus
    via?: OrderStatus
  } | null>(null)

  const { data: customer, isLoading } = useQuery({
    queryKey: queryKeys.customers.detail(customerId),
    queryFn: () => api.get<Customer>(`/customers/${customerId}`),
  })

  const { data: orders } = useQuery({
    queryKey: queryKeys.orders.list({ customerId }),
    queryFn: () =>
      api.get<{ items: OrderRow[] }>('/orders', { query: { customerId, pageSize: 50 } }),
  })

  const { data: devices } = useQuery({
    queryKey: queryKeys.customers.devices(customerId),
    queryFn: () => api.get<DeviceRow[]>(`/customers/${customerId}/devices`),
  })

  const { data: rentals } = useQuery({
    queryKey: queryKeys.rentals.list({ customerId }),
    queryFn: () =>
      api.get<{ items: RentalRow[] }>('/rentals', { query: { customerId, pageSize: 50 } }),
    enabled: can('rentals:read'),
  })

  if (isLoading || !customer) {
    return (
      <Page>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-4 h-64 w-full" />
      </Page>
    )
  }

  const addressText = customer.address
    ? [
        customer.address.line1,
        customer.address.line2,
        `${customer.address.city}, ${customer.address.state} ${customer.address.postalCode}`,
      ]
        .filter(Boolean)
        .join(', ')
    : null

  const spentCents = (orders?.items ?? [])
    .filter((o) => o.status !== 'cancelled' && o.status !== 'refunded')
    .reduce((sum, o) => sum + o.totalCents, 0)

  // Pending orders first — that's what the counter is looking for — with
  // completed (and cancelled/refunded) orders below, both newest first as the
  // API already returns them.
  const openOrders = (orders?.items ?? []).filter((o) => !CLOSED_ORDER_STATUSES.includes(o.status))
  const closedOrders = (orders?.items ?? []).filter((o) => CLOSED_ORDER_STATUSES.includes(o.status))

  return (
    <Page>
      <div className="pb-3">
        <Link
          to="/customers"
          className="inline-flex items-center gap-1.5 text-sm text-foreground-muted hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" aria-hidden="true" /> All customers
        </Link>
      </div>

      <PageHeader
        title={customer.name}
        description={
          [customer.businessName, customer.phone ? formatUsPhone(customer.phone) : customer.email]
            .filter(Boolean)
            .join(' — ') || undefined
        }
        actions={
          <>
            <MarkImportantButton itemType="customer" itemId={customerId} />
            {!customer.isActive ? <Badge tone="danger">Inactive</Badge> : null}
            {customer.taxExempt ? <Badge tone="info">Tax exempt</Badge> : null}
            {can('orders:write') && customer.isActive ? (
              <Button variant="primary" size="sm" asChild>
                <Link to="/orders/new" search={{ customerId }}>
                  <ShoppingCart /> New purchase
                </Link>
              </Button>
            ) : null}
            {can('support:write') ? <MessageCustomerButton customer={customer} /> : null}
            {can('customers:write') ? <InviteButton customer={customer} /> : null}
            {can('customers:write') ? (
              <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                Edit
              </Button>
            ) : null}
          </>
        }
      />

      <PinnedNotes customerId={customerId} />

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <Stat label="Orders" value={String(orders?.items.length ?? 0)} />
        <Stat label="Spent" value={formatMoney(spentCents)} />
        <Stat label="Devices" value={String(devices?.length ?? 0)} />
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="size-4" aria-hidden="true" /> Orders
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {orders?.items.length ? (
                <>
                  {openOrders.length ? (
                    <ul className="divide-y divide-border">
                      {openOrders.map((order) => (
                        <OrderListRow
                          key={order.id}
                          order={order}
                          canAct={can('orders:write')}
                          onAct={(to, via) => setTransition({ order, to, via })}
                        />
                      ))}
                    </ul>
                  ) : null}
                  {closedOrders.length ? (
                    <>
                      <p className="border-t border-border px-4 pt-3 pb-1 text-2xs uppercase tracking-wide text-foreground-subtle">
                        Completed
                      </p>
                      <ul className="divide-y divide-border">
                        {closedOrders.map((order) => (
                          <OrderListRow
                            key={order.id}
                            order={order}
                            canAct={can('orders:write')}
                            onAct={(to, via) => setTransition({ order, to, via })}
                          />
                        ))}
                      </ul>
                    </>
                  ) : null}
                </>
              ) : (
                <div className="p-6">
                  <EmptyState
                    icon={ShoppingCart}
                    title="No orders yet"
                    description="Anything they buy, in the shop or online, appears here."
                  />
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="size-4" aria-hidden="true" /> Their devices
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {devices?.length ? (
                <ul className="divide-y divide-border">
                  {devices.map((device) => (
                    <li key={device.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{device.productName}</span>
                        <span className="block font-identifier text-2xs text-foreground-muted">
                          IMEI {device.imei}
                          {device.orderNumber ? ` · ${device.orderNumber}` : ''}
                        </span>
                        <UnitAttributeBadges attributes={device.attributes} />
                      </span>
                      <Badge tone={UNIT_STATUS_TONE[device.status] ?? 'neutral'}>
                        {humanizeStatus(device.status)}
                      </Badge>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="p-6">
                  <EmptyState
                    icon={Smartphone}
                    title="No devices on record"
                    description="A handset appears here once it has been sold to them."
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {can('rentals:read') ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarClock className="size-4" aria-hidden="true" /> Rentals
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {rentals?.items.length ? (
                  <ul className="divide-y divide-border">
                    {rentals.items.map((rental) => (
                      <li key={rental.id}>
                        <Link
                          to="/rentals/$rentalId"
                          params={{ rentalId: rental.id }}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-surface-sunken"
                        >
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium">{rental.productName}</span>
                            <span className="block font-identifier text-2xs text-foreground-muted">
                              {rental.rentalNumber} · {rental.imei}
                              {rental.dueDate ? ` · due ${rental.dueDate}` : ''}
                            </span>
                          </span>
                          <Badge tone={RENTAL_STATUS_TONE[rental.status] ?? 'neutral'}>
                            {humanizeStatus(rental.status)}
                          </Badge>
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="p-6">
                    <EmptyState
                      icon={CalendarClock}
                      title="No rentals on record"
                      description="A phone or SIM rented to them appears here."
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          ) : null}

          {can('subscriptions:read') ? (
            <CustomerPlansCard
              customerId={customerId}
              canWrite={can('subscriptions:write')}
              canSell={can('orders:write') && customer.isActive}
              taxExempt={customer.taxExempt}
            />
          ) : null}

          <CustomerSupportTicketsCard customer={{ id: customerId, name: customer.name }} />

          <LinkedTasksCard customer={{ id: customerId, name: customer.name }} />
        </div>

        <div className="space-y-4">
          <CustomerNotesCard customerId={customerId} canWrite={can('customers:write')} />
          <PhoneNumbersCard customer={customer} canWrite={can('customers:write')} />
          <LinkedCustomersCard customer={customer} canWrite={can('customers:write')} />

          <SavedCardsCard customer={customer} canWrite={can('customers:write')} />

          <StoreCreditCard customerId={customerId} canAdjust={can('customers:credit')} />

          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Detail label="Name" value={customer.name} copy={customer.name} />
              <Detail
                label="Business / other identifier"
                value={customer.businessName ?? 'None on file'}
                copy={customer.businessName ?? undefined}
              />
              <Detail
                label="Email"
                value={customer.email ?? 'None on file'}
                copy={customer.email ?? undefined}
              />
              <Detail
                label="Address"
                value={addressText ?? 'None on file'}
                copy={addressText ?? undefined}
              />
              <Detail
                label="Customer since"
                value={new Date(customer.createdAt).toLocaleDateString('en-US', {
                  month: 'long',
                  year: 'numeric',
                })}
              />
              <Detail
                label="Origin"
                value={customer.origin ? ORIGIN_LABEL[customer.origin] : 'Unknown'}
              />
              <Detail label="Source" value={customer.source ?? 'None on file'} />
              <Detail
                label="External ID"
                value={customer.externalId ?? 'None on file'}
                mono
                copy={customer.externalId ?? undefined}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      <EditCustomerDialog customer={customer} open={editing} onOpenChange={setEditing} />
      {transition ? (
        <OrderTransitionDialog
          order={transition.order}
          to={transition.to}
          via={transition.via}
          onClose={() => setTransition(null)}
        />
      ) : null}
    </Page>
  )
}

/**
 * One order row, plus — when the order is open and the viewer can act — the
 * one counter button its status calls for. Editing a whole order still means
 * going to the order page; this is deliberately just "mark it paid" / "mark
 * it picked up".
 */
function OrderListRow({
  order,
  canAct,
  onAct,
}: {
  order: OrderRow
  canAct: boolean
  onAct: (to: OrderStatus, via?: OrderStatus) => void
}) {
  const action = canAct ? counterAction(order.status) : null

  return (
    <li className="flex items-center gap-3 px-4 py-2.5 text-sm">
      <Link
        to="/orders/$orderId"
        params={{ orderId: order.id }}
        className="flex min-w-0 flex-1 items-center gap-3 hover:underline"
      >
        <span className="font-identifier font-medium">{order.orderNumber}</span>
        <Badge tone={ORDER_STATUS_TONE[order.status] ?? 'neutral'}>
          {humanizeStatus(order.status)}
        </Badge>
        <span className="text-2xs text-foreground-muted">
          {new Date(order.createdAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </span>
      </Link>
      <span className="text-end tabular">
        <span className="font-medium">{formatMoney(order.totalCents)}</span>
        {order.balanceDueCents > 0 ? (
          <span className="block text-2xs text-warning">
            {formatMoney(order.balanceDueCents)} due
          </span>
        ) : null}
      </span>
      {action ? (
        <Button size="sm" variant="primary" onClick={() => onAct(action.to, action.via)}>
          {actionLabel(action.to)}
        </Button>
      ) : null}
    </li>
  )
}

/**
 * Sends an account-setup email — the way a customer who only ever had a phone
 * on file (or one who was added by hand) gets a password for the storefront.
 * When there is no email on record yet, asks for one first; the invite goes
 * out to whatever address is on file otherwise.
 */
function InviteButton({ customer }: { customer: Customer }) {
  const toast = useToast()
  const [asking, setAsking] = useState(false)
  const [email, setEmail] = useState(customer.email ?? '')

  const invite = useMutation({
    mutationFn: (targetEmail?: string) =>
      api.post(`/customers/${customer.id}/invite`, targetEmail ? { email: targetEmail } : {}),
    onSuccess: () => {
      toast.success(`Invite sent to ${customer.email ?? email}`)
      setAsking(false)
    },
    onError: (error: Error) => toast.error(error.message),
  })

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          if (customer.email) invite.mutate(undefined)
          else setAsking(true)
        }}
        disabled={invite.isPending}
      >
        {invite.isPending ? 'Sending…' : 'Send invite'}
      </Button>
      <Dialog open={asking} onOpenChange={setAsking}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add an email to invite {customer.name}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <p className="text-sm text-foreground-muted">
              They will get an email with a link to set a password and sign in.
            </p>
            <Field label="Email" required>
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAsking(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => invite.mutate(email.trim())}
              disabled={!email.trim() || invite.isPending}
            >
              {invite.isPending ? 'Sending…' : 'Send invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/**
 * Every number the shop has on file for this customer — the primary that
 * transactional texts go to, plus any extras (t0835), so a wife giving her
 * own number at the counter still finds her husband's account.
 */
function PhoneNumbersCard({ customer, canWrite }: { customer: Customer; canWrite: boolean }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [editingPhone, setEditingPhone] = useState<CustomerPhone | null>(null)
  const [confirmingPrimary, setConfirmingPrimary] = useState<CustomerPhone | null>(null)

  const { data: extras } = useQuery({
    queryKey: queryKeys.customers.phones(customer.id),
    queryFn: () => api.get<CustomerPhone[]>(`/customers/${customer.id}/phones`),
  })

  const invalidatePhones = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.customers.phones(customer.id) })
  const invalidateAll = () => queryClient.invalidateQueries({ queryKey: queryKeys.customers.all() })

  const remove = useMutation({
    mutationFn: (phoneId: string) => api.delete(`/customers/${customer.id}/phones/${phoneId}`),
    onSuccess: async () => {
      await invalidatePhones()
      toast.success('Removed')
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const makePrimary = useMutation({
    mutationFn: (phoneId: string) =>
      api.post(`/customers/${customer.id}/phones/${phoneId}/make-primary`, {}),
    onSuccess: async () => {
      await Promise.all([invalidatePhones(), invalidateAll()])
      toast.success('Primary number updated')
      setConfirmingPrimary(null)
    },
    onError: (error: Error) => toast.error(error.message),
  })

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Phone className="size-4" aria-hidden="true" /> Phone numbers
        </CardTitle>
        {canWrite ? (
          <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
            <Plus className="size-3.5" aria-hidden="true" /> Add
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="p-0">
        <ul className="divide-y divide-border">
          <li className="flex items-center gap-3 px-4 py-2.5 text-sm">
            <div className="min-w-0 flex-1">
              {customer.phone ? (
                <p className="font-identifier">{formatUsPhone(customer.phone)}</p>
              ) : (
                <p className="text-foreground-muted">No primary number — texts can't be sent</p>
              )}
            </div>
            {customer.phone && customer.phoneSmsCapable ? (
              <Badge tone="neutral">Texts OK</Badge>
            ) : null}
            {customer.phone ? <Badge tone="neutral">Primary</Badge> : null}
            {customer.phone ? (
              <CopyButton value={formatUsPhone(customer.phone)} label="phone number" />
            ) : null}
            {customer.phone ? <CallButton phone={customer.phone} /> : null}
          </li>
          {(extras ?? []).map((extra) => (
            <li key={extra.id} className="flex items-center gap-2 px-4 py-2.5 text-sm">
              <div className="min-w-0 flex-1">
                <p className="font-identifier">{formatUsPhone(extra.phone)}</p>
                <p className="text-2xs text-foreground-muted">{extra.label ?? 'No label'}</p>
              </div>
              {extra.smsCapable ? <Badge tone="neutral">Texts OK</Badge> : null}
              <CopyButton value={formatUsPhone(extra.phone)} label="phone number" />
              <CallButton phone={extra.phone} />
              {canWrite ? (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Edit label for ${formatUsPhone(extra.phone)}`}
                    onClick={() => setEditingPhone(extra)}
                  >
                    <Pencil className="size-3.5" aria-hidden="true" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirmingPrimary(extra)}>
                    Make primary
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove ${formatUsPhone(extra.phone)}`}
                    onClick={() => remove.mutate(extra.id)}
                  >
                    <Trash2 className="size-3.5" aria-hidden="true" />
                  </Button>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      </CardContent>

      <AddPhoneDialog
        customerId={customer.id}
        open={adding}
        onOpenChange={setAdding}
        onAdded={invalidatePhones}
      />
      <EditPhoneDialog
        phone={editingPhone}
        customerId={customer.id}
        onOpenChange={(open) => !open && setEditingPhone(null)}
        onSaved={invalidatePhones}
      />
      <Dialog
        open={!!confirmingPrimary}
        onOpenChange={(open) => !open && setConfirmingPrimary(null)}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Make this the primary number?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-muted">
              {confirmingPrimary ? formatUsPhone(confirmingPrimary.phone) : ''} becomes the number
              texts go to. Its texts setting comes with it.
              {customer.phone
                ? ` The current primary (${formatUsPhone(customer.phone)}) is kept as an extra number, with no label.`
                : ''}
              {confirmingPrimary?.label
                ? ` Its label ("${confirmingPrimary.label}") is dropped — a primary number doesn't carry one.`
                : ''}
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmingPrimary(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => confirmingPrimary && makePrimary.mutate(confirmingPrimary.id)}
              disabled={makePrimary.isPending}
            >
              {makePrimary.isPending ? 'Saving…' : 'Make primary'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

function AddPhoneDialog({
  customerId,
  open,
  onOpenChange,
  onAdded,
}: {
  customerId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onAdded: () => Promise<unknown>
}) {
  const toast = useToast()
  const [phone, setPhone] = useState('')
  const [label, setLabel] = useState('')
  const [smsCapable, setSmsCapable] = useState(false)
  const [phoneError, setPhoneError] = useState<string | undefined>()

  const reset = () => {
    setPhone('')
    setLabel('')
    setSmsCapable(false)
    setPhoneError(undefined)
  }

  const add = useMutation({
    mutationFn: () => {
      const parsed = addCustomerPhoneSchema.safeParse({
        phone,
        label: label.trim() || null,
        smsCapable,
      })
      if (!parsed.success) {
        throw new Error(parsed.error.issues[0]?.message ?? 'Enter a valid phone number')
      }
      return api.post(`/customers/${customerId}/phones`, parsed.data)
    },
    onSuccess: async () => {
      await onAdded()
      toast.success('Added')
      onOpenChange(false)
      reset()
    },
    onError: (error: Error) => {
      if (error instanceof ApiError && error.status === 409) setPhoneError(error.message)
      else toast.error(error.message)
    },
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) reset()
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Add a phone number</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Phone" required error={phoneError}>
            {(props) => (
              <Input
                {...props}
                type="tel"
                autoFocus
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value)
                  setPhoneError(undefined)
                }}
              />
            )}
          </Field>
          <Field label="Label" hint="e.g. Mobile, Home, Spouse">
            {(props) => (
              <Input {...props} value={label} onChange={(e) => setLabel(e.target.value)} />
            )}
          </Field>
          <div className="flex items-center justify-between rounded-md border border-border p-2.5">
            <span className="text-sm">Can receive texts</span>
            <Switch checked={smsCapable} onCheckedChange={setSmsCapable} />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => add.mutate()}
            disabled={!phone.trim() || add.isPending}
          >
            {add.isPending ? 'Adding…' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EditPhoneDialog({
  phone,
  customerId,
  onOpenChange,
  onSaved,
}: {
  phone: CustomerPhone | null
  customerId: string
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const toast = useToast()
  const [label, setLabel] = useState(phone?.label ?? '')
  const [smsCapable, setSmsCapable] = useState(phone?.smsCapable ?? false)

  useEffect(() => {
    setLabel(phone?.label ?? '')
    setSmsCapable(phone?.smsCapable ?? false)
  }, [phone])

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/customers/${customerId}/phones/${phone?.id}`, {
        label: label.trim() || null,
        smsCapable,
      }),
    onSuccess: () => {
      onSaved()
      toast.success('Saved')
      onOpenChange(false)
    },
    onError: (error: Error) => toast.error(error.message),
  })

  return (
    <Dialog open={!!phone} onOpenChange={(open) => !open && onOpenChange(false)}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Edit {phone ? formatUsPhone(phone.phone) : ''}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Label" hint="e.g. Mobile, Home, Spouse">
            {(props) => (
              <Input
                {...props}
                autoFocus
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            )}
          </Field>
          <div className="flex items-center justify-between rounded-md border border-border p-2.5">
            <span className="text-sm">Can receive texts</span>
            <Switch checked={smsCapable} onCheckedChange={setSmsCapable} />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface p-3">
      <p className="text-2xs uppercase tracking-wide text-foreground-subtle">{label}</p>
      <p className="mt-0.5 text-xl font-semibold tabular">{value}</p>
    </div>
  )
}

function Detail({
  label,
  value,
  mono,
  copy,
}: {
  label: string
  value: string
  mono?: boolean
  /** When set, a copy button puts this text on the clipboard. */
  copy?: string
}) {
  return (
    <div>
      <p className="text-2xs uppercase tracking-wide text-foreground-subtle">{label}</p>
      <div className="mt-0.5 flex items-center gap-1">
        <p className={`min-w-0 ${mono ? 'font-identifier' : ''} text-foreground-muted`}>{value}</p>
        {copy ? <CopyButton value={copy} label={label.toLowerCase()} /> : null}
      </div>
    </div>
  )
}

function EditCustomerDialog({
  customer,
  open,
  onOpenChange,
}: {
  customer: Customer
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [name, setName] = useState(customer.name)
  const [businessName, setBusinessName] = useState(customer.businessName ?? '')
  const [phone, setPhone] = useState(customer.phone ?? '')
  const [phoneSmsCapable, setPhoneSmsCapable] = useState(customer.phoneSmsCapable)
  const [email, setEmail] = useState(customer.email ?? '')
  const [address, setAddress] = useState(addressDraftFrom(customer.address))
  const [taxExempt, setTaxExempt] = useState(customer.taxExempt)
  const [source, setSource] = useState(customer.source ?? '')
  const [externalId, setExternalId] = useState(customer.externalId ?? '')

  // Re-seed on open so the form reflects the record as it is now, not as it
  // was when this page first mounted.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only on open — a background refetch must not wipe what staff are typing
  useEffect(() => {
    if (open) {
      setAddress(addressDraftFrom(customer.address))
      setSource(customer.source ?? '')
      setExternalId(customer.externalId ?? '')
    }
  }, [open])

  const save = useMutation({
    mutationFn: async () =>
      api.patch(`/customers/${customer.id}`, {
        name,
        businessName: businessName.trim() || null,
        phone: phone.trim() || null,
        phoneSmsCapable,
        email: email.trim() || null,
        address: addressFromDraft(address),
        taxExempt,
        source: source.trim() || null,
        externalId: externalId.trim() || null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.customers.all() })
      toast.success('Saved')
      onOpenChange(false)
    },
    onError: (error: Error) => toast.error(error.message),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit {customer.name}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Name" required>
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field
            label="Business / other identifier"
            hint="Shows in search and next to the name — use it to tell similar accounts apart."
          >
            {(props) => (
              <Input
                {...props}
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
              />
            )}
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Source" hint="Where this record came from, e.g. Square export.">
              {(props) => (
                <Input {...props} value={source} onChange={(e) => setSource(e.target.value)} />
              )}
            </Field>
            <Field label="External ID" hint="Its id in another system, e.g. a Square customer ID.">
              {(props) => (
                <Input
                  {...props}
                  value={externalId}
                  onChange={(e) => setExternalId(e.target.value)}
                />
              )}
            </Field>
          </div>
          <Field
            label="Primary phone"
            hint="Used for texts. Manage additional numbers on the customer page."
          >
            {(props) => (
              <Input
                {...props}
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            )}
          </Field>
          {phone.trim() ? (
            <div className="flex items-center justify-between rounded-md border border-border p-2.5">
              <span className="text-sm">Primary number can receive texts</span>
              <Switch checked={phoneSmsCapable} onCheckedChange={setPhoneSmsCapable} />
            </div>
          ) : null}
          <Field
            label="Email"
            hint="Sign-in uses this — changing it changes what they log in with."
          >
            {(props) => (
              <Input
                {...props}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>
          <CustomerAddressFields value={address} onChange={setAddress} />
          <div className="flex items-center justify-between rounded-md border border-border p-2.5">
            <span className="text-sm">
              Tax exempt
              <span className="block text-xs text-foreground-muted">
                No sales tax is charged on this customer's orders.
              </span>
            </span>
            <Switch checked={taxExempt} onCheckedChange={setTaxExempt} />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
